import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';
import DreamModal from './DreamModal';
import { Timestamp } from 'firebase/firestore';
import SEO from './SEO';

interface Dream {
  // Memory data properties
  id: string;
  userId: string;
  title: string;
  content: string;
  tags: string[];
  isPublic: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  authorName: string;
  
  // Visual properties for rendering
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  connections: string[];
  isExpanded: boolean;
}

interface DreamNode {
  id: string;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  radius: number;
  baseRadius: number;
  dream: Dream;
  connections: string[];
  isExpanded: boolean;
  lastUpdate: number;
  originalX: number;
  originalY: number;
}

interface DreamMapProps {
  userId?: string;
  showPrivate?: boolean;
}

interface Memory {
  id: string;
  title: string;
  content: string;
  authorId: string;
  authorName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isPublic: boolean;
  tags: string[];
}

interface BackgroundStar {
  x: number;
  y: number;
  radius: number;
  color: string;
  twinkleSpeed: number;
  twinkleOffset: number;
  type: 'normal' | 'nebula' | 'distant';
}

interface Node {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  originalX: number;
  originalY: number;
  targetX: number;
  targetY: number;
  baseRadius: number;
  radius: number;
  connections: string[];
  isExpanded: boolean;
  lastUpdate: number;
  isSelected: boolean;
  dream: Dream;
}

const DreamMap: React.FC<DreamMapProps> = ({ userId, showPrivate = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [hoveredNode, setHoveredNode] = useState<Node | null>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const animationFrameRef = useRef<number>();
  const mousePos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [expandedNode, setExpandedNode] = useState<string | null>(null);
  const backgroundStars = useRef<BackgroundStar[]>([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const [isExitVisible, setIsExitVisible] = useState(false);
  const [showDreamModal, setShowDreamModal] = useState(false);
  const [selectedDreamId, setSelectedDreamId] = useState<string | null>(null);
  const [connectedNodes, setConnectedNodes] = useState<Node[]>([]);
  const [selectedDream, setSelectedDream] = useState<Dream | null>(null);

  // Add debug ref to track updates
  const debugRef = useRef({
    lastUpdate: Date.now(),
    updateCount: 0,
    panCount: 0
  });

  // Add ref for pan offset to avoid state update delays
  const panOffsetRef = useRef({ x: 0, y: 0 });

  // Add ref to store the center position of expanded node
  const expandedCenterRef = useRef({ x: 0, y: 0 });

  // Add ref to track if we're dragging to prevent accidental constellation exits
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);

  // Add ref to store original positions
  const originalPositions = useRef<Map<string, { x: number, y: number }>>(new Map());

  // Add touch zoom state
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
  const [initialZoom, setInitialZoom] = useState<number>(1);

  // Add refs to track the bounds of the star cluster
  const clusterBounds = useRef({ minX: 0, maxX: 0, minY: 0, maxY: 0 });

  // Add function to calculate distance between two touch points
  const getTouchDistance = (touch1: Touch, touch2: Touch) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Update panOffsetRef when panOffset state changes
  useEffect(() => {
    panOffsetRef.current = panOffset;
  }, [panOffset]);

  useEffect(() => {
    // Generate background stars once
    const numStars = 200;
    const colors = [
      'rgba(255, 255, 255, 0.8)',  // White
      'rgba(173, 216, 230, 0.6)',  // Light blue
      'rgba(255, 182, 193, 0.6)',  // Light pink
      'rgba(221, 160, 221, 0.6)',  // Plum
    ];

    backgroundStars.current = Array.from({ length: numStars }, () => {
      const type = Math.random() < 0.1 ? 'nebula' : 
                  Math.random() < 0.3 ? 'distant' : 
                  'normal';
      return {
        x: (Math.random() - 0.5) * window.innerWidth * 4, // Wider distribution
        y: (Math.random() - 0.5) * window.innerHeight * 4, // Wider distribution
        radius: type === 'nebula' ? 2 + Math.random() * 3 :
               type === 'distant' ? 0.3 + Math.random() * 0.5 :
               0.8 + Math.random() * 1.2,
        color: colors[Math.floor(Math.random() * colors.length)],
        twinkleSpeed: 0.5 + Math.random() * 2,
        twinkleOffset: Math.random() * Math.PI * 2,
        type
      };
    });
    console.log('Background stars generated');
  }, []);

  // Update createInitialNodes to calculate cluster bounds
  const createInitialNodes = (dreams: Dream[]): Node[] => {
    const spiralSpacing = 80;
    const angleIncrement = Math.PI * (3 - Math.sqrt(5));
    
    const nodes = dreams.map((dream, index) => {
      const radius = spiralSpacing * Math.sqrt(index);
      const angle = index * angleIncrement;
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);
      
      // Update bounds
      clusterBounds.current.minX = Math.min(clusterBounds.current.minX, x);
      clusterBounds.current.maxX = Math.max(clusterBounds.current.maxX, x);
      clusterBounds.current.minY = Math.min(clusterBounds.current.minY, y);
      clusterBounds.current.maxY = Math.max(clusterBounds.current.maxY, y);
      
      return {
        id: dream.id,
        x,
        y,
        vx: 0,
        vy: 0,
        radius: 2 + Math.random() * 1.5,
        connections: [] as string[],
        isExpanded: false,
        dream,
        originalX: x,
        originalY: y,
        targetX: x,
        targetY: y,
        baseRadius: 2 + Math.random() * 1.5,
        lastUpdate: Date.now(),
        isSelected: false,
      } as Node;
    });

    return nodes;
  };

  useEffect(() => {
    const fetchDreams = async () => {
      try {
        const dreamsRef = collection(db, 'dreams');
        const constraints = [
          userId ? where('userId', '==', userId) : where('isPublic', '==', true)
        ];
        if (!showPrivate && userId === user?.uid) {
          constraints.push(where('isPublic', '==', true));
        }
        const q = query(dreamsRef, ...constraints);
        const snapshot = await getDocs(q);
        
        const dreams = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Dream));

        const newNodes = createInitialNodes(dreams);
        
        // Connect nodes based on shared tags
        newNodes.forEach(node => {
          newNodes.forEach(otherNode => {
            if (node.id !== otherNode.id) {
              const sharedTags = node.dream.tags.filter(tag => 
                otherNode.dream.tags.includes(tag)
              );
              if (sharedTags.length > 0) {
                node.connections.push(otherNode.id);
              }
            }
          });
        });

        setNodes(newNodes);
        
        // Center the view on load
        if (canvasRef.current) {
          setPanOffset({
            x: canvasRef.current.width / 2,
            y: canvasRef.current.height / 2
          });
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching dreams:', error);
        setIsLoading(false);
      }
    };

    fetchDreams();
  }, [userId, user, showPrivate]);

  // Store original positions when nodes are first created
  useEffect(() => {
    if (nodes.length > 0) {
      const positions = new Map();
      nodes.forEach(node => {
        positions.set(node.id, { x: node.x, y: node.y });
      });
      originalPositions.current = positions;
    }
  }, [nodes.length]);

  // Update initial positioning effect
  useEffect(() => {
    if (canvasRef.current && nodes.length > 0) {
      const canvas = canvasRef.current;
      
      // Calculate cluster center
      const clusterCenterX = (clusterBounds.current.minX + clusterBounds.current.maxX) / 2;
      const clusterCenterY = (clusterBounds.current.minY + clusterBounds.current.maxY) / 2;
      
      // Set initial pan offset to center the cluster
      const newPanOffset = {
        x: canvas.width / 2 - clusterCenterX,
        y: canvas.height / 2 - clusterCenterY
      };
      
      // Apply initial position immediately
      panOffsetRef.current = newPanOffset;
      setPanOffset(newPanOffset);
    }
  }, [nodes.length]);

  // Update the zoom handler to maintain mouse position
  const handleCanvasWheel = (e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const delta = -e.deltaY;
    const zoomFactor = 0.1;
    const newZoom = Math.max(0.5, Math.min(5, zoomLevel * (1 + Math.sign(delta) * zoomFactor)));
    
    if (newZoom !== zoomLevel) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Calculate world position of mouse before zoom
      const worldX = (mouseX - panOffsetRef.current.x) / zoomLevel;
      const worldY = (mouseY - panOffsetRef.current.y) / zoomLevel;
      
      // Calculate new pan offset to keep mouse position fixed in world space
      const newPanOffset = {
        x: mouseX - worldX * newZoom,
        y: mouseY - worldY * newZoom
      };

      // Update zoom and pan
      setZoomLevel(newZoom);
      panOffsetRef.current = newPanOffset;
      setPanOffset(newPanOffset);
    }
  };

  // Update boundary constraints
  const updatePanOffset = (newPanOffset: { x: number, y: number }) => {
    if (!canvasRef.current) return newPanOffset;

    const canvas = canvasRef.current;
    const padding = 200; // Increased padding for better boundaries
    
    // Calculate cluster bounds in screen space
    const minX = clusterBounds.current.minX * zoomLevel;
    const maxX = clusterBounds.current.maxX * zoomLevel;
    const minY = clusterBounds.current.minY * zoomLevel;
    const maxY = clusterBounds.current.maxY * zoomLevel;
    
    // Calculate the size of the cluster
    const clusterWidth = maxX - minX;
    const clusterHeight = maxY - minY;
    
    // Calculate the center position that would keep the cluster in view
    const idealX = (canvas.width - clusterWidth) / 2 - minX;
    const idealY = (canvas.height - clusterHeight) / 2 - minY;
    
    // Calculate the maximum allowed distance from the ideal position
    const maxOffsetX = Math.max(clusterWidth / 2, canvas.width / 2) + padding;
    const maxOffsetY = Math.max(clusterHeight / 2, canvas.height / 2) + padding;
    
    // Constrain the pan offset
    return {
      x: Math.min(Math.max(newPanOffset.x, idealX - maxOffsetX), idealX + maxOffsetX),
      y: Math.min(Math.max(newPanOffset.y, idealY - maxOffsetY), idealY + maxOffsetY)
    };
  };

  // Update handleMouseMove to only apply boundary constraints during drag
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const worldX = (x - panOffsetRef.current.x) / zoomLevel;
    const worldY = (y - panOffsetRef.current.y) / zoomLevel;

    if (isDragging.current) {
      const dx = e.clientX - lastMousePos.current.x;
      const dy = e.clientY - lastMousePos.current.y;
      
      // Apply new pan offset directly during drag
      const newPanOffset = {
        x: panOffsetRef.current.x + dx,
        y: panOffsetRef.current.y + dy
      };
      
      panOffsetRef.current = newPanOffset;
      setPanOffset(newPanOffset);
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    }

    // Update hover detection
    let closestNode: Node | null = null;
    let closestDistance = Infinity;

    nodes.forEach(node => {
      const dx = node.x - worldX;
      const dy = node.y - worldY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const hitRadius = getClickableRadius(node);

      if (distance < hitRadius && distance < closestDistance) {
        closestNode = node;
        closestDistance = distance;
      }
    });

    setHoveredNode(closestNode);
  };

  // Update the animation logic to properly track and update positions
  useEffect(() => {
    if (!canvasRef.current || nodes.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Make canvas focusable for better event handling
    canvas.tabIndex = 1;

    // Add resize handler
    const resizeCanvas = () => {
      const oldWidth = canvas.width;
      const oldHeight = canvas.height;
      
      // Set canvas size to match display size
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      
      // Adjust pan offset to maintain relative position
      const newPanOffset = {
        x: (panOffsetRef.current.x * canvas.width) / oldWidth,
        y: (panOffsetRef.current.y * canvas.height) / oldHeight
      };
      panOffsetRef.current = newPanOffset;
      setPanOffset(newPanOffset);
    };

    // Initial resize
    resizeCanvas();

    // Add event listeners
    canvas.addEventListener('wheel', handleCanvasWheel, { passive: false });
    window.addEventListener('resize', resizeCanvas);

    let lastFrameTime = Date.now();
    const animate = () => {
      const currentTime = Date.now();
      const deltaTime = (currentTime - lastFrameTime) / 1000;
      lastFrameTime = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.translate(panOffsetRef.current.x, panOffsetRef.current.y);
      ctx.scale(zoomLevel, zoomLevel);

      // Create a rich night sky background
      const bgWidth = canvas.width / zoomLevel;
      const bgHeight = canvas.height / zoomLevel;
      const bgX = -panOffsetRef.current.x / zoomLevel;
      const bgY = -panOffsetRef.current.y / zoomLevel;

      // Create a gradient background
      const gradient = ctx.createRadialGradient(
        bgX + bgWidth/2, bgY + bgHeight/2, 0,
        bgX + bgWidth/2, bgY + bgHeight/2, bgWidth/1.5
      );
      gradient.addColorStop(0, '#0B1026');    // Dark blue center
      gradient.addColorStop(0.5, '#0A0F20');  // Slightly darker
      gradient.addColorStop(1, '#080C1A');    // Even darker edge

      ctx.fillStyle = gradient;
      ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

      // Draw nebula effects
      const nebulaTime = currentTime * 0.0001;
      backgroundStars.current.forEach((star: BackgroundStar) => {
        if (star.type === 'nebula') {
          const nebulaGradient = ctx.createRadialGradient(
            star.x, star.y, 0,
            star.x, star.y, star.radius * 20
          );
          const alpha = (Math.sin(nebulaTime + star.twinkleOffset) * 0.2 + 0.5) * 0.1;
          nebulaGradient.addColorStop(0, `rgba(147, 197, 253, ${alpha})`);
          nebulaGradient.addColorStop(0.5, `rgba(139, 92, 246, ${alpha * 0.5})`);
          nebulaGradient.addColorStop(1, 'rgba(139, 92, 246, 0)');
          
          ctx.fillStyle = nebulaGradient;
          ctx.fillRect(
            star.x - star.radius * 20,
            star.y - star.radius * 20,
            star.radius * 40,
            star.radius * 40
          );
        }
      });

      // Draw background stars with enhanced effects
      backgroundStars.current.forEach((star: BackgroundStar) => {
        const adjustedX = (star.x - panOffsetRef.current.x) / zoomLevel;
        const adjustedY = (star.y - panOffsetRef.current.y) / zoomLevel;
        
        if (adjustedX >= bgX && adjustedX <= bgX + bgWidth &&
            adjustedY >= bgY && adjustedY <= bgY + bgHeight) {
          const twinkle = Math.sin(currentTime * 0.001 * star.twinkleSpeed + star.twinkleOffset) * 0.3 + 0.7;
          
          if (star.type === 'normal') {
            // Draw main star
            ctx.beginPath();
            ctx.arc(adjustedX, adjustedY, star.radius / zoomLevel, 0, Math.PI * 2);
            ctx.fillStyle = star.color.replace(/[\d.]+\)$/g, `${twinkle})`);
            ctx.fill();

            // Draw star rays
            if (star.radius > 1) {
              const rayLength = star.radius * 2;
              const rayCount = 4;
              const rayAngle = (currentTime * 0.0005) % (Math.PI * 2);
              
              ctx.strokeStyle = star.color.replace(/[\d.]+\)$/g, `${twinkle * 0.5})`);
              ctx.lineWidth = (0.5 / zoomLevel);
              
              for (let i = 0; i < rayCount; i++) {
                const angle = (i * Math.PI / rayCount) + rayAngle;
                ctx.beginPath();
                ctx.moveTo(
                  adjustedX + Math.cos(angle) * star.radius,
                  adjustedY + Math.sin(angle) * star.radius
                );
                ctx.lineTo(
                  adjustedX + Math.cos(angle) * (star.radius + rayLength),
                  adjustedY + Math.sin(angle) * (star.radius + rayLength)
                );
                ctx.stroke();
              }
            }
          } else if (star.type === 'distant') {
            // Simple dot for distant stars
            ctx.beginPath();
            ctx.arc(adjustedX, adjustedY, star.radius / zoomLevel, 0, Math.PI * 2);
            ctx.fillStyle = star.color.replace(/[\d.]+\)$/g, `${twinkle * 0.6})`);
            ctx.fill();
          }
        }
      });

      // Calculate new positions for nodes
      const updatedNodes = nodes.map(node => {
        const isExpanded = node.id === expandedNode;
        const isConnectedToExpanded = expandedNode && node.connections.includes(expandedNode);
        
        // Keep current positions unless explicitly moved
        let targetX = node.x;
        let targetY = node.y;

        // Calculate base size and target size with MUCH larger scaling
        const baseSize = 15; // Significantly increased base size
        const targetSize = isExpanded ? baseSize * 12 : // Much larger when expanded
                         isConnectedToExpanded ? baseSize * 8 : // Larger for connected nodes
                         baseSize;

        // Only calculate new positions when expansion state changes
        if (node.isExpanded !== isExpanded) {
            if (isExpanded) {
                targetX = node.originalX;
                targetY = node.originalY;
            } else if (isConnectedToExpanded) {
                const expandedNodeData = nodes.find(n => n.id === expandedNode);
                if (expandedNodeData) {
                    const connectedNodes = nodes.filter(n => n.connections.includes(expandedNode));
                    const index = connectedNodes.findIndex(n => n.id === node.id);
                    const totalNodes = connectedNodes.length;
                    const angle = (index / totalNodes) * Math.PI * 2;
                    const radius = 300; // Increased spacing
                    
                    targetX = expandedNodeData.originalX + Math.cos(angle) * radius;
                    targetY = expandedNodeData.originalY + Math.sin(angle) * radius;
                }
            } else {
                targetX = node.originalX;
                targetY = node.originalY;
            }
        }

        // Faster interpolation for smoother size changes
        const interpolationSpeed = 0.02;
        const newX = node.x + (targetX - node.x) * interpolationSpeed;
        const newY = node.y + (targetY - node.y) * interpolationSpeed;

        // Faster size interpolation
        const sizeInterpolationSpeed = 0.15;
        const newRadius = node.radius + (targetSize - node.radius) * sizeInterpolationSpeed;

        return {
            ...node,
            x: newX,
            y: newY,
            radius: newRadius,
            isExpanded
        };
      });

      // Update nodes state if positions have changed
      const hasChanges = updatedNodes.some((node, i) => 
        Math.abs(node.x - nodes[i].x) > 0.01 || 
        Math.abs(node.y - nodes[i].y) > 0.01 ||
        Math.abs(node.radius - nodes[i].radius) > 0.01
      );

      if (hasChanges) {
        setNodes(updatedNodes);
      }

      // Draw connections with enhanced effects
      updatedNodes.forEach(node => {
        node.connections.forEach(connectionId => {
          const connectedNode = updatedNodes.find(n => n.id === connectionId);
          if (connectedNode) {
            const isHighlighted = expandedNode && 
              (node.id === expandedNode || connectionId === expandedNode);
            const isSelectedConnection = selectedNode && 
              (node.id === selectedNode.id || connectionId === selectedNode.id) &&
              expandedNode;
            
            const dx = connectedNode.x - node.x;
            const dy = connectedNode.y - node.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            const time = currentTime * 0.001;
            const shimmer = Math.sin(time * 2 + distance * 0.05) * 0.2 + 0.8;
            
            const gradient = ctx.createLinearGradient(
              node.x, node.y,
              connectedNode.x, connectedNode.y
            );

            if (isSelectedConnection) {
              // Blue theme for selected star's connections
              gradient.addColorStop(0, `rgba(59, 130, 246, ${0.8 * shimmer})`);  // Blue-500
              gradient.addColorStop(0.2, `rgba(96, 165, 250, ${0.9 * shimmer})`); // Blue-400
              gradient.addColorStop(0.5, `rgba(147, 197, 253, ${1.0 * shimmer})`); // Blue-200
              gradient.addColorStop(0.8, `rgba(96, 165, 250, ${0.9 * shimmer})`); // Blue-400
              gradient.addColorStop(1, `rgba(59, 130, 246, ${0.8 * shimmer})`);  // Blue-500
              ctx.lineWidth = 3;
              ctx.shadowColor = '#3B82F6';
              ctx.shadowBlur = 15;
            } else if (isHighlighted) {
              gradient.addColorStop(0, `rgba(139, 92, 246, ${0.8 * shimmer})`);
              gradient.addColorStop(0.2, `rgba(167, 139, 250, ${0.9 * shimmer})`);
              gradient.addColorStop(0.5, `rgba(186, 230, 253, ${1.0 * shimmer})`);
              gradient.addColorStop(0.8, `rgba(167, 139, 250, ${0.9 * shimmer})`);
              gradient.addColorStop(1, `rgba(139, 92, 246, ${0.8 * shimmer})`);
              ctx.lineWidth = 3;
              ctx.shadowColor = '#8B5CF6';
              ctx.shadowBlur = 10;
            } else {
              gradient.addColorStop(0, `rgba(139, 92, 246, ${0.3 * shimmer})`);
              gradient.addColorStop(0.2, `rgba(167, 139, 250, ${0.4 * shimmer})`);
              gradient.addColorStop(0.5, `rgba(186, 230, 253, ${0.5 * shimmer})`);
              gradient.addColorStop(0.8, `rgba(167, 139, 250, ${0.4 * shimmer})`);
              gradient.addColorStop(1, `rgba(139, 92, 246, ${0.3 * shimmer})`);
              ctx.lineWidth = 2;
              ctx.shadowColor = '#8B5CF6';
              ctx.shadowBlur = 5;
            }

            // Draw the main connection line
            ctx.beginPath();
            ctx.strokeStyle = gradient;
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(connectedNode.x, connectedNode.y);
            ctx.stroke();

            // Draw particles along the connection
            const particleCount = Math.floor(distance / 50);
            for (let i = 0; i < particleCount; i++) {
              const t = (time * 0.5 + i / particleCount) % 1;
              const x = node.x + dx * t;
              const y = node.y + dy * t;
              const particleSize = (Math.sin(t * Math.PI) * 2 + 2) / zoomLevel;
              
              ctx.beginPath();
              ctx.arc(x, y, particleSize, 0, Math.PI * 2);
              ctx.fillStyle = isSelectedConnection 
                ? `rgba(147, 197, 253, ${0.6 * shimmer * (1 - t)})` // Blue particles
                : `rgba(186, 230, 253, ${0.6 * shimmer * (1 - t)})`; // Default particles
              ctx.fill();
            }
          }
        });
      });

      // Draw nodes with enhanced star effects
      updatedNodes.forEach(node => {
        const isSelected = node.id === selectedDreamId;
        const isExpanded = node.id === expandedNode;
        const isConnectedToExpanded = expandedNode && node.connections.includes(expandedNode);
        
        // Calculate base sizes with much larger values
        const baseRadius = node.radius;
        const maxGlowRadius = Math.min(120, baseRadius * 10); // Doubled glow size
        const maxCoronaRadius = Math.min(400, baseRadius * 25); // Doubled corona size
        
        // Draw outer glow with larger constrained size
        ctx.beginPath();
        const glowRadius = isExpanded ? maxGlowRadius : 
                         isConnectedToExpanded ? maxGlowRadius * 0.8 : 
                         maxGlowRadius * 0.6;
        
        const time = currentTime * 0.001;
        const pulseIntensity = Math.sin(time * (isExpanded ? 3 : 2) + node.x * 0.1) * 0.3 + 0.7;
        
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, glowRadius
        );
        
        if (isSelected) {
          // Enhanced blue theme for selected node with constrained effects
          gradient.addColorStop(0, `rgba(255, 255, 255, ${pulseIntensity})`);
          gradient.addColorStop(0.2, `rgba(147, 197, 253, ${pulseIntensity * 0.8})`);
          gradient.addColorStop(0.4, `rgba(59, 130, 246, ${pulseIntensity * 0.6})`);
          gradient.addColorStop(0.6, `rgba(37, 99, 235, ${pulseIntensity * 0.4})`);
          gradient.addColorStop(1, 'rgba(30, 64, 175, 0)');
          ctx.shadowColor = '#3B82F6';
          ctx.shadowBlur = Math.min(15 / zoomLevel, baseRadius * 2);

          // Add corona effect with constrained size
          const coronaCount = 12;
          const coronaTime = time * 0.5;
          const maxRayLength = 50 / zoomLevel; // Maximum ray length in pixels
          
          for (let i = 0; i < coronaCount; i++) {
            const angle = (coronaTime + i * (Math.PI * 2 / coronaCount)) % (Math.PI * 2);
            const waveOffset = Math.sin(coronaTime * 2 + i) * Math.min(5 / zoomLevel, baseRadius);
            const innerRadius = Math.min(20 / zoomLevel, baseRadius * 4) + waveOffset;
            const outerRadius = Math.min(innerRadius + maxRayLength, maxCoronaRadius);
            
            const startX = node.x + Math.cos(angle) * innerRadius;
            const startY = node.y + Math.sin(angle) * innerRadius;
            const endX = node.x + Math.cos(angle) * outerRadius;
            const endY = node.y + Math.sin(angle) * outerRadius;
            
            const coronaGradient = ctx.createLinearGradient(startX, startY, endX, endY);
            coronaGradient.addColorStop(0, `rgba(147, 197, 253, ${0.2 * pulseIntensity})`);
            coronaGradient.addColorStop(0.4, `rgba(59, 130, 246, ${0.15 * pulseIntensity})`);
            coronaGradient.addColorStop(1, 'rgba(29, 78, 216, 0)');
            
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.strokeStyle = coronaGradient;
            ctx.lineWidth = Math.min(1.5 / zoomLevel, baseRadius * 0.2);
            ctx.stroke();
          }
        } else if (isExpanded || isConnectedToExpanded) {
          // Purple theme with constrained effects
          gradient.addColorStop(0, `rgba(255, 255, 255, ${pulseIntensity})`);
          gradient.addColorStop(0.3, `rgba(186, 230, 253, ${pulseIntensity * 0.6})`);
          gradient.addColorStop(0.6, `rgba(167, 139, 250, ${pulseIntensity * 0.4})`);
          gradient.addColorStop(1, 'rgba(139, 92, 246, 0)');
          ctx.shadowColor = '#7C3AED';
          ctx.shadowBlur = Math.min(8 / zoomLevel, baseRadius * 1.5);
        } else {
          // Default theme with minimal effects
          gradient.addColorStop(0, `rgba(255, 255, 255, ${pulseIntensity * 0.8})`);
          gradient.addColorStop(0.3, `rgba(186, 230, 253, ${pulseIntensity * 0.5})`);
          gradient.addColorStop(0.6, `rgba(167, 139, 250, ${pulseIntensity * 0.3})`);
          gradient.addColorStop(1, 'rgba(139, 92, 246, 0)');
          ctx.shadowColor = 'rgba(139, 92, 246, 0.3)';
          ctx.shadowBlur = Math.min(5 / zoomLevel, baseRadius);
        }
        
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw sparkles with constrained size
        const sparkleCount = isExpanded ? 8 : isConnectedToExpanded ? 6 : 4;
        const sparkleTime = time * 5 + node.x * 0.2;
        const maxSparkleSize = Math.min(1 / zoomLevel, baseRadius * 0.2);
        const maxSparkleDistance = Math.min(15 / zoomLevel, baseRadius * 3);
        
        for (let i = 0; i < sparkleCount; i++) {
          const angle = (sparkleTime + i * (Math.PI * 2 / sparkleCount)) % (Math.PI * 2);
          const distance = Math.min(maxSparkleDistance, baseRadius * (1.5 + Math.sin(sparkleTime + i) * 0.3));
          const x = node.x + Math.cos(angle) * distance;
          const y = node.y + Math.sin(angle) * distance;
          const sparkleSize = (Math.sin(sparkleTime + i) * 0.3 + 0.7) * maxSparkleSize;

          ctx.beginPath();
          ctx.arc(x, y, sparkleSize, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${pulseIntensity * 0.5})`;
          ctx.fill();
        }

        // Draw star core with constrained size
        const coreRadius = Math.min(3 / zoomLevel, baseRadius * 0.7);
        ctx.beginPath();
        ctx.arc(node.x, node.y, coreRadius, 0, Math.PI * 2);
        const coreGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, coreRadius
        );
        
        coreGradient.addColorStop(0, `rgba(255, 255, 255, ${pulseIntensity})`);
        coreGradient.addColorStop(0.3, `rgba(186, 230, 253, ${pulseIntensity * 0.9})`);
        coreGradient.addColorStop(0.6, `rgba(167, 139, 250, ${pulseIntensity * 0.8})`);
        coreGradient.addColorStop(1, `rgba(139, 92, 246, ${pulseIntensity * 0.7})`);
        
        ctx.fillStyle = coreGradient;
        ctx.fill();

        // Add outer glow ring with constrained size
        const outerGlowRadius = Math.min(40 / zoomLevel, baseRadius * 8);
        ctx.beginPath();
        ctx.arc(node.x, node.y, outerGlowRadius, 0, Math.PI * 2);
        const outerGlow = ctx.createRadialGradient(
          node.x, node.y, outerGlowRadius * 0.5,
          node.x, node.y, outerGlowRadius
        );
        outerGlow.addColorStop(0, `rgba(139, 92, 246, ${0.15 * pulseIntensity})`);
        outerGlow.addColorStop(1, 'rgba(139, 92, 246, 0)');
        ctx.fillStyle = outerGlow;
        ctx.fill();
      });

      ctx.restore();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      canvas.removeEventListener('wheel', handleCanvasWheel);
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [nodes.length, expandedNode, zoomLevel]);

  // Update initial pan offset on component mount
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const newPanOffset = {
        x: canvas.width / 2,
        y: canvas.height / 2
      };
      panOffsetRef.current = newPanOffset;
      setPanOffset(newPanOffset);
    }
  }, []);

  // Update centerOnNode to maintain original positions
  const centerOnNode = (node: Node) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    
    // Calculate new pan offset to center the view on the node's original position
    const newPanOffset = {
      x: canvas.width / 2 - node.originalX * zoomLevel,
      y: canvas.height / 2 - node.originalY * zoomLevel
    };
    
    panOffsetRef.current = newPanOffset;
    setPanOffset(newPanOffset);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (e.button === 0) {
      isDragging.current = true;
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      lastMousePos.current = { x: e.clientX, y: e.clientY };
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.style.cursor = 'grabbing';
        canvas.focus();
      }
    }
  };

  // Update the click detection radius based on zoom and state
  const getClickableRadius = (node: Node) => {
    const isExpanded = node.id === expandedNode;
    const isConnectedToExpanded = expandedNode && node.connections.includes(expandedNode);
    
    // Increase click detection area, especially for expanded and connected nodes
    const baseRadius = isExpanded ? 40 : 
                      isConnectedToExpanded ? 30 : 
                      15;
    return baseRadius / zoomLevel;
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging.current) {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.style.cursor = 'grab';
      }
      
      // Only handle as a click if we haven't dragged much
      if (dragStartPos.current) {
        const dx = e.clientX - dragStartPos.current.x;
        const dy = e.clientY - dragStartPos.current.y;
        const dragDistance = Math.sqrt(dx * dx + dy * dy);
        
        // If we've dragged less than 5 pixels, treat it as a click
        if (dragDistance < 5) {
          handleCanvasClick();
        }
      }
    }
    isDragging.current = false;
    dragStartPos.current = null;
  };

  // Move handleCanvasClick definition before its usage
  const handleCanvasClick = () => {
    if (!hoveredNode) return;

    const isConnectedToExpanded = expandedNode && hoveredNode.connections.includes(expandedNode);
    
    // Update selected node immediately
    setSelectedNode(hoveredNode);
    setSelectedDreamId(hoveredNode.id);

    if (!expandedNode || isConnectedToExpanded) {
      setExpandedNode(hoveredNode.id);
      setIsExitVisible(true);
      
      // Center the view on the node's original position with animation
      const startPanOffset = { ...panOffsetRef.current };
      const canvas = canvasRef.current;
      if (canvas) {
        const targetPanOffset = {
          x: canvas.width / 2 - hoveredNode.originalX * zoomLevel,
          y: canvas.height / 2 - hoveredNode.originalY * zoomLevel
        };
        
        const startTime = Date.now();
        const duration = 1000; // 1 second animation
        
        const animatePan = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const t = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
          
          const newPanOffset = {
            x: startPanOffset.x + (targetPanOffset.x - startPanOffset.x) * t,
            y: startPanOffset.y + (targetPanOffset.y - startPanOffset.y) * t
          };
          
          panOffsetRef.current = newPanOffset;
          setPanOffset(newPanOffset);
          
          if (progress < 1) {
            requestAnimationFrame(animatePan);
          }
        };
        
        animatePan();
      }
    }
  };

  // Update touch event handlers
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 2) {
        // Initialize pinch-zoom
        const distance = getTouchDistance(e.touches[0], e.touches[1]);
        setInitialPinchDistance(distance);
        setInitialZoom(zoomLevel);
      } else if (e.touches.length === 1) {
        isDragging.current = true;
        const touch = e.touches[0];
        dragStartPos.current = { x: touch.clientX, y: touch.clientY };
        lastMousePos.current = { x: touch.clientX, y: touch.clientY };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 2 && initialPinchDistance !== null) {
        // Handle pinch-zoom
        const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
        const scale = currentDistance / initialPinchDistance;
        const newZoom = Math.max(0.5, Math.min(5, initialZoom * scale));

        // Calculate the midpoint between the two touches
        const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        const rect = canvas.getBoundingClientRect();
        
        // Calculate world position of the midpoint before zoom
        const worldX = (midX - rect.left - panOffsetRef.current.x) / zoomLevel;
        const worldY = (midY - rect.top - panOffsetRef.current.y) / zoomLevel;
        
        // Calculate new pan offset to keep midpoint fixed in world space
        const newPanOffset = {
          x: midX - rect.left - worldX * newZoom,
          y: midY - rect.top - worldY * newZoom
        };

        // Update zoom and pan with boundary constraints
        setZoomLevel(newZoom);
        const constrainedPanOffset = updatePanOffset(newPanOffset);
        setPanOffset(constrainedPanOffset);
        panOffsetRef.current = constrainedPanOffset;
      } else if (e.touches.length === 1 && isDragging.current) {
        // Handle single touch drag
        const touch = e.touches[0];
        const dx = touch.clientX - lastMousePos.current.x;
        const dy = touch.clientY - lastMousePos.current.y;
        
        // Calculate new pan offset with boundary constraints
        const newPanOffset = updatePanOffset({
          x: panOffsetRef.current.x + dx,
          y: panOffsetRef.current.y + dy
        });
        
        // Update pan offset
        panOffsetRef.current = newPanOffset;
        setPanOffset(newPanOffset);
        
        // Update last mouse position
        lastMousePos.current = { x: touch.clientX, y: touch.clientY };

        // Calculate world coordinates for hover detection
        const rect = canvas.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        const worldX = (x - newPanOffset.x) / zoomLevel;
        const worldY = (y - newPanOffset.y) / zoomLevel;

        // Update hovered node
        let closestNode: Node | null = null;
        let closestDistance = Infinity;

        nodes.forEach(node => {
          const nodeDx = node.x - worldX;
          const nodeDy = node.y - worldY;
          const distance = Math.sqrt(nodeDx * nodeDx + nodeDy * nodeDy);
          const hitRadius = getClickableRadius(node);

          if (distance < hitRadius && distance < closestDistance) {
            closestNode = node;
            closestDistance = distance;
          }
        });

        setHoveredNode(closestNode);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length < 2) {
        setInitialPinchDistance(null);
      }
      if (e.touches.length === 0) {
        if (isDragging.current && dragStartPos.current) {
          const touch = e.changedTouches[0];
          const dx = touch.clientX - dragStartPos.current.x;
          const dy = touch.clientY - dragStartPos.current.y;
          const dragDistance = Math.sqrt(dx * dx + dy * dy);
          
          if (dragDistance < 10) {
            const rect = canvas.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;
            const worldX = (x - panOffsetRef.current.x) / zoomLevel;
            const worldY = (y - panOffsetRef.current.y) / zoomLevel;

            // Find the node under the touch point
            let clickedNode: Node | null = null;
            let minDistance = Infinity;

            nodes.forEach(node => {
              const dx = node.x - worldX;
              const dy = node.y - worldY;
              const distance = Math.sqrt(dx * dx + dy * dy);
              const hitRadius = getClickableRadius(node);

              if (distance < hitRadius && distance < minDistance) {
                clickedNode = node;
                minDistance = distance;
              }
            });

            if (clickedNode) {
              // If we're clicking the same node that's already hovered, handle the click
              if (hoveredNode && clickedNode.id === hoveredNode.id) {
                handleCanvasClick();
              } else {
                // Otherwise, just update the hovered node
                setHoveredNode(clickedNode);
              }
            }
          }
        }
        isDragging.current = false;
        dragStartPos.current = null;
      }
    };

    // Add non-passive touch event listeners
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [nodes, zoomLevel, handleCanvasClick, initialPinchDistance, initialZoom]);

  // Add styles for the exit button container
  const exitButtonStyles = {
    position: 'fixed' as const,
    top: '80px', // Move slightly higher to avoid overlap
    left: '20px', // Move to left side instead of right
    zIndex: 50,
  };

  // Update handleExitConstellation to only clear constellation state
  const handleExitConstellation = () => {
    setExpandedNode(null);
    setSelectedNode(null);
    setSelectedDreamId(null);
    setIsExitVisible(false);
  };

  // Update the View Full Dream button click handler
  const handleViewDream = (node: Node) => {
    setSelectedDream(node.dream);
    setShowDreamModal(true);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-dream-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-dream-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Dream Constellation Map - DriftRealm"
        description="Explore your dreams in an interactive constellation map. Connect dreams through shared themes and discover patterns in your subconscious mind."
        url="/dreams/map"
      />
      <div className="relative w-full h-full">
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none select-none cursor-grab active:cursor-grabbing"
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ touchAction: 'none' }}
        />
        
        <AnimatePresence>
          <div key="ui-layer" className="fixed inset-0 pointer-events-none">
            {isExitVisible && (
              <div style={exitButtonStyles} className="pointer-events-auto">
                <motion.button
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  onClick={handleExitConstellation}
                  className="bg-dream-800/80 hover:bg-dream-700/80 text-dream-100 px-4 py-2 rounded-lg 
                           shadow-lg backdrop-blur-sm flex items-center gap-2 transition-all
                           hover:shadow-dream-400/20 hover:scale-105
                           md:text-base text-sm"
                >
                  <span>Exit Constellation</span>
                </motion.button>
              </div>
            )}

            {hoveredNode && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="pointer-events-auto fixed p-4 rounded-lg bg-dream-800/90 
                         border border-dream-600/30 shadow-lg backdrop-blur-md z-10
                         md:max-w-xs max-w-[calc(100vw-2rem)] mx-2
                         hover:shadow-dream-400/20 hover:border-dream-500/40"
                style={{
                  left: Math.min(
                    panOffsetRef.current.x + hoveredNode.x * zoomLevel, 
                    window.innerWidth - (window.innerWidth < 768 ? 32 : 300)
                  ),
                  top: Math.min(
                    panOffsetRef.current.y + hoveredNode.y * zoomLevel, 
                    window.innerHeight - 200
                  )
                }}
              >
                <h3 className="text-dream-50 font-medium mb-2 md:text-base text-sm">{hoveredNode.dream.title}</h3>
                {hoveredNode.dream.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {hoveredNode.dream.tags.map((tag, index) => (
                      <motion.span
                        key={`tag-${index}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="px-2 py-0.5 text-xs rounded-full
                               bg-dream-700/40 text-dream-200
                               border border-dream-600/20
                               hover:bg-dream-600/40 hover:border-dream-500/30
                               transition-colors"
                      >
                        {tag}
                      </motion.span>
                    ))}
                  </div>
                )}
                <div className="text-xs text-dream-400">
                  {expandedNode ? 
                    hoveredNode.connections.includes(expandedNode) ?
                      "Tap to explore this dream's constellation" :
                      "Part of another constellation" :
                    "Tap to explore connections"}
                </div>
              </motion.div>
            )}

            {selectedNode && (
              <motion.div
                initial={{ opacity: 0, x: 20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="pointer-events-auto fixed md:top-20 md:right-4 md:left-auto
                         left-4 right-4 bottom-4 md:bottom-auto p-6 rounded-lg
                         bg-dream-800/90 border border-dream-600/30
                         shadow-lg backdrop-blur-md z-10 md:max-w-md w-auto
                         hover:shadow-dream-400/20 hover:border-dream-500/40"
              >
                <h3 className="text-xl font-medium text-dream-50 mb-2">
                  {selectedNode.dream.title}
                </h3>
                <p className="text-dream-300 text-sm mb-4">
                  Dreamed by {selectedNode.dream.authorName}
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleViewDream(selectedNode)}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium
                           bg-dream-600/60 text-dream-50
                           border border-dream-500/30
                           hover:bg-dream-500/60 hover:border-dream-400/40
                           hover:shadow-lg hover:shadow-dream-400/20
                           transition-all duration-300"
                >
                  View Full Dream
                </motion.button>
              </motion.div>
            )}

            {/* Modal Overlay */}
            {showDreamModal && selectedDream && (
              <div className="fixed inset-0 z-[9999] overflow-hidden">
                <div 
                  className="fixed inset-0 bg-black/70 backdrop-blur pointer-events-auto"
                  onClick={() => setShowDreamModal(false)}
                />
                <div className="fixed inset-0 z-[10000] overflow-y-auto pointer-events-none">
                  <div className="flex min-h-full items-center justify-center p-4">
                    <div 
                      className="relative bg-white rounded-lg shadow-xl md:max-w-2xl w-full mx-auto pointer-events-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DreamModal
                        memory={selectedDream}
                        onClose={() => {
                          setSelectedDream(null);
                          setShowDreamModal(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </AnimatePresence>
      </div>
    </>
  );
};

export default DreamMap; 